import { useState } from 'react';
import { useLocation } from 'wouter';
import { useQueryClient } from '@tanstack/react-query';
import { Layout } from '@/components/layout';
import { Button, Input, Select, Label } from '@/components/ui-elements';
import { useToast } from '@/hooks/use-toast';
import { useAuthHeaders } from '@/lib/auth-store';
import { ArrowLeft, Workflow } from 'lucide-react';
import { 
  useListStores, 
  useCreateFlow,
  getListFlowsQueryKey
} from '@workspace/api-client-react';

export function FlowNew() {
  const [_, setLocation] = useLocation();
  const authOpts = useAuthHeaders();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: stores } = useListStores({ request: authOpts });
  const createMutation = useCreateFlow({ request: authOpts });

  const [formData, setFormData] = useState({
    name: '',
    sourceStoreId: '',
    destStoreId: '',
    actionType: 'sync_inventory' as const,
    schedule: '',
    isActive: true
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.sourceStoreId === formData.destStoreId) {
      toast({ title: "Invalid mapping", description: "Source and destination stores cannot be the same.", variant: "destructive" });
      return;
    }

    try {
      await createMutation.mutateAsync({ 
        data: { 
          ...formData, 
          schedule: formData.schedule.trim() || null 
        } 
      });
      toast({ title: "Flow created successfully" });
      queryClient.invalidateQueries({ queryKey: getListFlowsQueryKey() });
      setLocation('/flows');
    } catch (err: any) {
      toast({ title: "Failed to create flow", description: err.message, variant: "destructive" });
    }
  };

  return (
    <Layout>
      <div className="mb-8">
        <button 
          onClick={() => setLocation('/flows')}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Flows
        </button>
        <h1 className="text-3xl font-display text-gradient mb-2 flex items-center gap-3">
          <Workflow className="w-8 h-8 text-primary" /> Create New Flow
        </h1>
        <p className="text-muted-foreground">Set up a new automated sync between your stores.</p>
      </div>

      <div className="max-w-2xl glass-panel rounded-3xl p-8 border border-border shadow-[0_0_40px_rgba(0,0,0,0.3)]">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label>Flow Name</Label>
            <Input 
              placeholder="e.g. Master Inventory to Wholesale" 
              value={formData.name} 
              onChange={e => setFormData({ ...formData, name: e.target.value })} 
              required 
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 bg-muted/50 rounded-2xl border border-border/50 relative">
            {/* Visual connector */}
            <div className="hidden md:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-primary/20 border border-primary text-primary flex items-center justify-center z-10">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
            </div>

            <div className="space-y-2">
              <Label className="text-primary">Source Store (Reads data)</Label>
              <Select 
                value={formData.sourceStoreId} 
                onChange={e => setFormData({ ...formData, sourceStoreId: e.target.value })}
                required
              >
                <option value="" disabled>Select origin store...</option>
                {stores?.map(s => <option key={s.id} value={s.id}>{s.nickname} ({s.storeUrl})</option>)}
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-emerald-400">Destination Store (Writes data)</Label>
              <Select 
                value={formData.destStoreId} 
                onChange={e => setFormData({ ...formData, destStoreId: e.target.value })}
                required
              >
                <option value="" disabled>Select target store...</option>
                {stores?.map(s => <option key={s.id} value={s.id}>{s.nickname} ({s.storeUrl})</option>)}
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Action Type</Label>
            <Select 
              value={formData.actionType} 
              onChange={e => setFormData({ ...formData, actionType: e.target.value as any })}
              required
            >
              <option value="sync_inventory">Sync Inventory (Levels & Locations)</option>
              <option value="sync_products">Sync Products (Titles, Desc, Images)</option>
              <option value="sync_prices">Sync Prices (Regular & Compare at)</option>
              <option value="sync_orders">Sync Orders (Create drafts for fulfillment)</option>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">Matching is done via SKU by default.</p>
          </div>

          <div className="space-y-2">
            <Label>Schedule (Optional Cron Expression)</Label>
            <Input 
              placeholder="0 * * * * (Every hour)" 
              value={formData.schedule} 
              onChange={e => setFormData({ ...formData, schedule: e.target.value })} 
            />
            <p className="text-xs text-muted-foreground mt-1">Leave blank for manual triggering only.</p>
          </div>

          <div className="pt-6 border-t border-border/50 flex gap-4">
            <Button type="button" variant="ghost" onClick={() => setLocation('/flows')}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1" isLoading={createMutation.isPending}>
              Create Automation Flow
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
